/**
 * What the signed-in caller's ROLE lets them do — the client-side mirror of the API's role gates.
 *
 * 🔴 THIS IS NOT AUTHORIZATION. Every rule below is enforced by the server, which returns 403 whether
 * or not the button was rendered; a browser can call the route directly and nothing here would know.
 * The job of this module is the OTHER failure: offering a person an action they are not permitted to
 * take. A hidden control is a courtesy, a 403 is the control — and if the two ever disagree, the
 * server is right and this file is the bug.
 *
 * ⚠️ WHY IT EXISTS AT ALL. `#768` closed the viewer write-gate on `analyses` — the one API surface
 * that had none — and the UI kept advertising the actions. A `viewer` landed on `/`, read the H1
 * "Run a deal analysis", filled the form, and got a 403 for their trouble. The permission was real
 * and enforced; the product simply had not been told. That is the mirror image of
 * [[built-but-nothing-reads-it]]: a rule that IS read, by everything except the screen.
 *
 * ⚠️ IT MATTERS NOW BECAUSE OF WHO HOLDS `viewer`. Brad ruled 2026-08-21 that clients READ the
 * reports GGP produces and do not submit deals — the twelve-month shape, not a first-client
 * concession. `viewer` is the role that expresses it, so these are the rules that decide what a
 * paying client's product looks like, not an internal permissions detail.
 *
 * 🔴 FOUR SETS THAT ARE CURRENTLY EQUAL IN PAIRS, AND STAY SEPARATE ANYWAY — the same discipline
 * `analyses.ts` writes down for `ANALYSIS_SUBMIT_ROLES` vs `projects.ts`'s `INTAKE_ROLES`: they
 * answer different questions ("who may spend a model run" vs "who may create a record", canon R1)
 * and are free to diverge. Collapsing them on today's coincidence would couple two rules silently,
 * and the divergence would land as a permissions bug on a client's screen.
 */

import type { SessionClaims } from './api.js';

/**
 * Read the caller's membership role off the permissive session claims.
 *
 * ⚠️ `SessionClaims` is `Record<string, unknown>` — the claim is checked for being a string rather
 * than cast to one, so a malformed or absent claim reads as "no role" and every capability below
 * fails CLOSED. A cast would make `undefined` sail through as a role that matches nothing, which
 * happens to behave the same way today and would stop doing so the moment a set is inverted.
 */
export function roleOf(claims: SessionClaims): string | null {
  const role = claims['role'];
  return typeof role === 'string' ? role : null;
}

/** Who may START or STOP an underwrite — mirrors the api's `ANALYSIS_SUBMIT_ROLES`. */
const ANALYSIS_SUBMIT_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/**
 * Who may create a project record — mirrors `projects.ts` ("a viewer cannot create a record").
 * Creating a record is an employee act (canon R1: a person opens a pursuit or project).
 */
const PROJECT_INTAKE_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/**
 * Who may RETRIEVE a document — mirrors the api's `RETRIEVAL_ROLES` (canon R4: "employees retrieve
 * files through the PEE").
 *
 * ⚠️ Equal to `PROJECT_INTAKE_ROLES` today and kept separate deliberately: they answer different
 * questions — who may put a file IN versus who may take one OUT — and collapsing them on today's
 * coincidence would move both the day one diverges.
 *
 * ⚠️ Lives HERE rather than in a page. It was a local const in `ProjectView.tsx`, and the document
 * find surface needed the same answer — a second copy would have started the drift ADR-0055 spent an
 * increment collapsing (twelve `Set`s of role strings carrying two policies between them). One
 * definition, two readers.
 */
const RETRIEVAL_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller submit or cancel an underwrite? */
export function canSubmitAnalysis(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && ANALYSIS_SUBMIT_ROLES.has(role);
}

/**
 * May this caller take a document OUT of the record?
 *
 * The server refuses it either way (403 on the retrieval route). Both halves matter: hiding without
 * the 403 is theatre, and the 403 without hiding is a trap that teaches people the product is broken.
 */
export function canRetrieveDocument(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && RETRIEVAL_ROLES.has(role);
}

/**
 * Who may ASK the record a question — mirrors the api's gate on `POST /projects/:code/ask`, which is
 * `model:spend` AND `retrieval:read` (ADR-0045): an answer spends a model call, and it narrates passages
 * of the SOURCE documents a viewer is refused the download of. Stage 3.5 rules the conversation
 * staff-only.
 *
 * ⚠️ Equal to the sets above today and kept separate for the reason the header gives — and with one
 * more: this is a CONJUNCTION of two permissions, so the day either one narrows, this set must follow,
 * and it can only do that if it is its own set.
 */
const ASK_RECORD_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller ask the record a question? The server's 403 is the control; this is the courtesy. */
/**
 * Who may START a design or technical review — mirrors the review routes, which require BOTH
 * `model:spend` and `register:import` (the operator roles). Kept apart from `ANALYSIS_SUBMIT_ROLES`
 * although equal today: "who may spend on an underwrite" and "who may land review findings on a
 * record" are different questions, free to diverge.
 */
const REVIEW_RUN_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller start a design or technical review on a record? The server refuses a viewer. */
export function canRunReview(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && REVIEW_RUN_ROLES.has(role);
}

/**
 * Who may RE-UNDERWRITE a record on demand — mirrors `POST /projects/:code/reunderwrite`, which requires
 * BOTH `model:spend` (it is a paid underwrite) and `project:intake` (it re-judges the record, the same
 * weight as putting a document in or taking one out). A conjunction, so its own set: the day either
 * permission narrows, this one must follow, and it can only do that if nothing else shares it.
 */
const REUNDERWRITE_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller start a re-underwrite of a record? The server refuses a viewer with a 403. */
export function canReunderwrite(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && REUNDERWRITE_ROLES.has(role);
}

/**
 * Who may REQUEST the deal-report PDF — mirrors `POST /projects/:code/report`, which requires
 * `project:intake` (the render files a document into the project's workspace). Its own set, so the day
 * that permission or this act moves, this follows without dragging anything else with it.
 *
 * ⚠️ Role only. The server ALSO refuses every tenant but GGP's own (`tenant_not_allowed`), which the
 * session cannot see; the panel shows that refusal in words rather than pretending to know it.
 */
const REPORT_REQUEST_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller request a deal report? The server refuses a viewer with a 403. */
export function canRequestReport(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && REPORT_REQUEST_ROLES.has(role);
}

/**
 * Who may CONFIRM or REJECT a protected-stop candidate — mirrors `stop:attest` in the api's permission
 * table, which ADR-0063 Q2 gave to the OWNER ALONE by ruling. The one role set here a ruling created
 * rather than a consolidation, so it is its own set and never folded into the approver roles.
 */
const STOP_ATTEST_ROLES: ReadonlySet<string> = new Set(['owner']);

/** May this caller decide a protected-stop candidate (ADR-0063)? The server refuses anyone else. */
export function canAttestStop(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && STOP_ATTEST_ROLES.has(role);
}

/** Owner-only by ruling (ADR-0071 / ADR-0059 D2) — mirrors the api's `basis:lock`. */
const BASIS_LOCK_ROLES: ReadonlySet<string> = new Set(['owner']);

/** May this caller lock or re-lock a project's basis? The server refuses anyone else. */
export function canLockBasis(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && BASIS_LOCK_ROLES.has(role);
}

/**
 * Who may DISPUTE a decision — mirrors `decision:dispute` (owner, admin, member). A viewer is refused
 * by ruling (ADR-0076 Q1): it is the role a client holds, and a client's objection reaches the record
 * through a GGP person who files it.
 */
const DISPUTE_FILE_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'member']);

/** May this caller dispute a Decision Record? The server refuses a viewer with a 403. */
export function canDisputeDecision(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && DISPUTE_FILE_ROLES.has(role);
}

/**
 * Who may ANSWER a dispute — mirrors `challenge:answer` (owner, admin), the permission the answer route
 * checks. Equal to the CRP approver roles today and kept separate, for the reason the header gives.
 */
const DISPUTE_ANSWER_ROLES: ReadonlySet<string> = new Set(['owner', 'admin']);

/** May this caller answer a dispute? The server ALSO refuses the person who filed it (403). */
export function canAnswerDispute(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && DISPUTE_ANSWER_ROLES.has(role);
}

/** The caller's user id off the session claims, or null. Checked, not cast — see {@link roleOf}. */
export function userIdOf(claims: SessionClaims): string | null {
  const id = claims['userId'];
  return typeof id === 'string' && id !== '' ? id : null;
}

export function canAskRecord(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && ASK_RECORD_ROLES.has(role);
}

/**
 * Who may create a record that ALREADY CARRIES its number — mirrors `projects.ts`'s `APPROVER_ROLES`.
 *
 * ⚠️ A STRICTLY SMALLER SET THAN `PROJECT_INTAKE_ROLES`, and that gap is the point: a `member` may
 * open a pursuit but may not migrate a numbered project, because migrating is a route to a project
 * number that skips promotion and carries the same authority as the IC decision itself (R5/R-e).
 */
const PROJECT_MIGRATE_ROLES: ReadonlySet<string> = new Set(['owner', 'admin']);

/** May this caller open a new project record? */
export function canCreateProject(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && PROJECT_INTAKE_ROLES.has(role);
}

/**
 * May this caller bring in a record that already has its GGP project number?
 *
 * Used by the portfolio import, where the class is decided by the LOG rather than chosen by the
 * reader — so a row a member could never create is knowable before it is offered. The intake form
 * states the same rule in prose instead, because there the reader picks the class and the server's
 * 403 is the honest answer to a choice they just made.
 */
export function canMigrateNumberedProject(claims: SessionClaims): boolean {
  const role = roleOf(claims);
  return role !== null && PROJECT_MIGRATE_ROLES.has(role);
}
